import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import {
  User,
  Flag,
  Calendar,
  Eye,
  Ruler,
  Weight,
  Droplets,
  Cigarette,
  Wine,
  Flower,
  Users
} from 'lucide-react';
import { EscortProfile } from '@/types';
import type { LucideIcon } from 'lucide-react';

interface PersonalInformationSectionProps {
  profile: EscortProfile;
  t: (key: string) => string;
}

// Utility component/function to render a detail item
const DetailItem: React.FC<{
  Icon: LucideIcon;
  label: string;
  value: React.ReactNode;
  condition?: boolean;
  iconRotation?: number;
}> = ({ Icon, label, value, condition = true, iconRotation }) => {
  if (!condition || !value) return null;
  return (
    <div className="flex items-center">
      <Icon 
        size={18} 
        className="mr-2 text-pink-500" 
        style={iconRotation ? { transform: `rotate(${iconRotation}deg)` } : {}}
      />
      <span className="text-muted-foreground w-32">{label}:</span>
      <span className="font-medium">{value}</span>
    </div>
  );
};

export const PersonalInformationSection: React.FC<PersonalInformationSectionProps> = ({ profile, t }) => {
  const attributes = profile.attributes;
  
  return (
    <Card className="mb-6 border-pink-100">
      <CardContent className="p-0">
        <div className="bg-pink-50 py-3 px-6 border-b border-pink-100">
          <h3 className="text-lg font-medium text-pink-700">{t('profile.personal_information')}</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
            <DetailItem Icon={User} label={t('gender')} value={attributes.gender} />
            <DetailItem Icon={Calendar} label={t('age')} value={`${profile.age} ${t('profile.years')}`} />
            <DetailItem Icon={Flag} label={t('nationality')} value={attributes.nationality} />
            <DetailItem Icon={Users} label={t('ethnicity')} value={attributes.ethnicity} />
            <DetailItem Icon={Eye} label={t('eye_color')} value={attributes.eyeColor} />
            <DetailItem Icon={Droplets} label={t('hair_color')} value={attributes.hairColor} />
            <DetailItem Icon={Ruler} label={t('height')} value={attributes.height ? `${attributes.height} ${t('unit_cm')}` : null} />
            <DetailItem Icon={Weight} label={t('weight')} value={attributes.weight ? `${attributes.weight} ${t('unit_kg')}` : null} />
            <DetailItem Icon={Ruler} label={t('measurements')} value={attributes.measurements} iconRotation={90} />
            {/* Cup size doesn't need an icon based on original code */}
            {attributes.cupSize && (
              <div className="flex items-center">
                {/* Placeholder rose icon */}
                <Flower size={18} className="mr-2 text-pink-500" />
                <span className="text-muted-foreground w-32">{t('cup_size')}:</span>
                <span className="font-medium">{attributes.cupSize}</span>
              </div>
            )}
            <DetailItem Icon={Flower} label={t('intimate_area')} value={attributes.intimateArea} />
            <DetailItem Icon={Cigarette} label={t('smoking')} value={attributes.smoking} />
            <DetailItem Icon={Wine} label={t('drinking')} value={attributes.drinking} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}; 